import React, { useState, useCallback } from 'react';
import { getRolesHavePermission } from '$/utils/hasPermission';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { MyRole } from '$/GraphQL/schema/types';

interface PermissionContextProps {
  isModalOpen: boolean;
  showPermissionModal?: (path: string) => void;
}

const PermissionContext = React.createContext<PermissionContextProps>({} as PermissionContextProps);

const PermissionModal: React.FC<{ roles: string[]; onClose: () => void }> = ({
  roles,
  onClose,
}) => {
  const roleNames = roles.map((role, index) => {
    if (index !== 0) return `${role}`;
    const prefix = [
      MyRole.Admin.toString(),
      MyRole.Editor.toString(),
      MyRole.Ops.toString(),
    ].includes(role)
      ? 'an'
      : 'a';
    return `${prefix} ${role}`;
  });
  return (
    <Modal className='md:min-w-45e'>
      <Modal.Body>
        <div className='flex flex-col justify-center items-center w-full text-base'>
          <div className='w-full text-center tracking-wide my-4'>
            You must be {roleNames.join(' or ')} to access this page.
          </div>
          <Button.rounded className='mt-1e' onClick={onClose}>
            OK
          </Button.rounded>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const PermissionProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [rolesCanAccess, setRolesCanAccess] = useState<string[]>([]);

  const showPermissionModal = useCallback((path: string) => {
    setIsOpenModal(true);
    const roles = getRolesHavePermission(path);
    setRolesCanAccess(roles);
  }, []);

  const onClose = useCallback(() => {
    setIsOpenModal(false);
    setRolesCanAccess([]);
  }, []);

  const value = React.useMemo(
    () => ({ showPermissionModal, isModalOpen: isOpenModal }),
    [showPermissionModal, isOpenModal]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
      {isOpenModal && <PermissionModal roles={rolesCanAccess} onClose={onClose} />}
    </PermissionContext.Provider>
  );
};

const usePermissionModal = (): PermissionContextProps => {
  const context = React.useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissionModal must be used within a PermissionProvider');
  }

  return context;
};

export { PermissionProvider, usePermissionModal };
